import { Box, LinearProgressBar, Text } from '@wix/design-system';
import React from 'react';

import { AI_IMAGE_MODAL_FEED_LOADER } from '../data-hooks';
import styles from './ai-image-modal-scss';

const GREY_LOADER_GIF_SRC =
  'https://static.wixstatic.com/media/977934_dc09a6a38d8445f1994b1d5736899a96~mv2.gif';

const WHITE_LOADER_GIF_SRC =
  'https://static.wixstatic.com/media/977934_2ddbf4faffce475f9d9011b98abc464b~mv2.gif';

type LoaderProps = {
  width: number | string;
  height: number | string;
  paddingLeft: React.ComponentProps<typeof Box>['paddingLeft'];
  paddingRight: React.ComponentProps<typeof Box>['paddingRight'];
  paddingTop: React.ComponentProps<typeof Box>['paddingTop'];
  paddingBottom: React.ComponentProps<typeof Box>['paddingBottom'];
  maxProgress: number;
  duration: number;
  showPercentage?: boolean;
  backgroundColor: 'grey' | 'white';
  loadingText: string;
};

export const Loader: React.FC<LoaderProps> = ({
  width,
  height,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  maxProgress,
  duration,
  showPercentage,
  backgroundColor,
  loadingText,
}) => {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    if (progress >= 93) return;

    const intervalDuration = 100; // Update every 100ms
    const totalSteps = duration / intervalDuration;
    const increment = maxProgress / totalSteps;

    const interval = setInterval(() => {
      setProgress(prevProgress => {
        const nextProgress = Math.min(prevProgress + increment, maxProgress);
        if (nextProgress >= maxProgress) {
          clearInterval(interval);
        }
        return nextProgress;
      });
    }, intervalDuration);

    return () => clearInterval(interval);
  }, [progress]);

  return (
    <Box
      width="100%"
      paddingLeft={paddingLeft}
      paddingRight={paddingRight}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
      dataHook={AI_IMAGE_MODAL_FEED_LOADER}
    >
      <Box
        width={width}
        height={height}
        backgroundColor={backgroundColor === 'grey' ? '#eff3f6' : backgroundColor}
        direction="vertical"
        align="center"
        verticalAlign="middle"
        borderRadius={8}
        gap="SP2"
      >
        <Box>
          <img
            width={82}
            height={82}
            src={backgroundColor === 'grey' ? GREY_LOADER_GIF_SRC : WHITE_LOADER_GIF_SRC}
            alt={loadingText}
          />
        </Box>
        <Box direction="horizontal" gap="SP3">
          <Box width={100} verticalAlign="middle" className={styles.progressIndicator}>
            <LinearProgressBar value={progress} />
          </Box>
          {showPercentage && (
            <Box width={20} verticalAlign="middle">
              <Text size="tiny" weight="bold">
                {Math.floor(progress)}%
              </Text>
            </Box>
          )}
        </Box>
        {loadingText && <Text size="tiny">{loadingText}</Text>}
      </Box>
    </Box>
  );
};
